import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Typography,
  makeStyles,
  Dialog,
  Backdrop,
  Fade,
  Tooltip
} from '@material-ui/core';
import { HomeShortcuts } from './HomeShortcuts';
import { Link } from 'react-router-dom';
import SystemHealth from './SystemHealth';
import { KpisObjetivos } from './KpisObjetivos';
import AlertasActivas from './AlertasActivas';
import ListObjectiveModal from '../../Dashboard/components/ListObjectiveModal';
import { useQueries } from '@tanstack/react-query';
import { HomePanel } from './HomePanel';
import { useKpisObjetivo } from '../../../hooks/useKpisObjetivo';
import { useRendimientoObjetivo } from '../../../hooks/useRendimientoObjetivo';
import { useDisponibilidadDetalle } from '../../../hooks/useDisponibilidadDetalle';
import { fetchLastStatusObjectiveByNode } from '../../../hooks/useLastStatusObjectiveByNode';

import codes_ok from '../../../data/codes_ok';
import { getApiKeys } from '../../../utils/fns/get-api-keys';
import { getQueryParams } from '../../../utils/fns/get-query-params';
import { ObjectivesApdexPanel } from './ObjectivesApdexPanel';
import moment from 'moment';
import 'moment/locale/es';
import { BenchmarkPanel } from './BenchmarkPanel';
import { HoneycombPanel } from './HoneycombPanel';

// Static initializations — run once at module load, not per render
moment.locale('es');
const OK_CODES_SET = new Set(codes_ok.map(c => c.id));

const useStyles = makeStyles((theme) => ({
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    height: '420px',
    '& > *': {
      flex: 1,
      height: '100%',
    }
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  gridSection: {
    flex: 1,
    minHeight: 0,
  },
  shortcutsWrapper: {
    width: '100%',
    padding: '5px',
    flexShrink: 0,
  },
  badgeCounter: {
    backgroundColor: '#F1F5F9',
    padding: '4px 12px',
    borderRadius: '20px',
    border: '1px solid #E2E8F0',
  },
  select: {
    minWidth: '200px',
    '& .MuiSelect-select': {
      padding: '6px 12px',
      fontSize: '0.875rem',
      backgroundColor: '#F9FAFB',
      borderRadius: '20px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid #E5E7EB',
      borderRadius: '20px',
    },
  },
  linkHover: {
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    }
  },
}));

const objectiveNameStyle = {
  fontWeight: 'normal',
  color: '#6B7280',
  fontSize: '0.9em',
  marginLeft: '4px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '300px',
  display: 'inline-block',
};

const PanelTitleWithObjective = React.memo(({ labelKey, objectiveName, to }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const inner = (
    <>
      {t(labelKey)}:
      <Tooltip title={objectiveName || '-'} placement="top" arrow>
        <span style={objectiveNameStyle}>
          {objectiveName || '-'}
        </span>
      </Tooltip>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes.linkHover} style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
        {inner}
      </Link>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', color: 'inherit' }}>
      {inner}
    </span>
  );
});

export function FixedPanels({ monitorsData, objective }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalData, setModalData] = React.useState({ id: null, name: '' });

  const objectiveId = objective?.objetivo_id;
  const objectiveName = objective?.nombre;

  const { data: kpiData, isLoading: isLoadingKpis } = useKpisObjetivo(objectiveId, { refetchInterval: 600000, retry: false, refetchOnWindowFocus: false });
    const { data: rendimientoData, isLoading: isLoadingRendimiento } = useRendimientoObjetivo(objectiveId, { refetchInterval: 600000, retry: false, refetchOnWindowFocus: false });
    const { data: disponibilidadDetalle, isLoading: isLoadingDetalle } = useDisponibilidadDetalle(objectiveId, { refetchInterval: 600000, retry: false, refetchOnWindowFocus: false });

  const categorizedMonitors = React.useMemo(() => monitorsData || {}, [monitorsData]);
  const isLoadingObjectives = !monitorsData || Object.keys(monitorsData).length === 0;

  const { user, token } = getApiKeys();

  const alertObjectives = React.useMemo(() => (categorizedMonitors ? [
    ...(categorizedMonitors.Downtime || []),
    ...(categorizedMonitors.Partial || categorizedMonitors.Parcial || [])
  ] : []), [categorizedMonitors]);

  const statusQueries = useQueries({
    queries: alertObjectives.map(obj => ({
      queryKey: ['lastStatusObjectiveByNode', user, obj.objetivo_id],
      queryFn: () => fetchLastStatusObjectiveByNode(user, token, obj.objetivo_id),
      enabled: !!user && !!token,
      refetchInterval: 600000,
    }))
  });

  const activeAlerts = React.useMemo(() => {
    const alerts = [];
    const processedAlerts = new Set();

    statusQueries.forEach((query, index) => {
      if (!query.data) return;
      
      const statusData = query.data;
      const originalObj = alertObjectives[index];
      if (!originalObj) return;

      (statusData.nodos || []).forEach(nodo => {
        if (nodo.status !== 'uptime' && nodo.status !== 'ok') {
          (nodo.pasos || []).forEach((paso, pasoIdx) => {
            const errors = (paso.codigo_descripcion || []).filter(err => !OK_CODES_SET.has(err.codigo_id));
            
            if (errors.length > 0) {
              const uniqueKey = `${originalObj.objetivo_id || ''}-${nodo.nodo_id || ''}-${paso.id_paso || pasoIdx}`;
              
              if (!processedAlerts.has(uniqueKey)) {
                processedAlerts.add(uniqueKey);
                
                let alertType = (paso.estado || nodo.status || 'downtime').toLowerCase();
                if (alertType === 'parcial') alertType = 'partial';
                
                alerts.push({
                  id: `${alertType}-${originalObj.objetivo_id || ''}-${nodo.nodo_id || ''}-${paso.id_paso || pasoIdx}`,
                  objetivo_id: originalObj.objetivo_id,
                  monitorNombre: originalObj.nombre || '',
                  pasoNombre: `${paso.nombre_paso || 'Paso ' + pasoIdx} (${nodo.titulo || nodo.nombre || ''})`,
                  errors: errors,
                  tiempo: moment(nodo.fecha_tz).fromNow(),
                  fecha_raw: new Date(nodo.fecha_tz),
                  tipo: alertType
                });
              }
            }
          });
        }
      });
    });

    return alerts.sort((a, b) => b.fecha_raw - a.fecha_raw);
  }, [statusQueries, alertObjectives]);

  const isLoadingAlerts = statusQueries.some(q => q.isLoading);

  const handleOpenModal = React.useCallback((id, name) => {
    setModalData({ id, name });
    setModalOpen(true);
  }, []);

  const handleCloseModal = React.useCallback(() => {
    setModalOpen(false);
  }, []);

  const dataPanels = React.useMemo(() => {
    const uptimeCount = categorizedMonitors?.Uptime?.length || 0;
    const downtimeCount = categorizedMonitors?.Downtime?.length || 0;
    const partialCount = (categorizedMonitors?.Partial?.length || 0) + (categorizedMonitors?.Parcial?.length || 0);
    const noMonitoringCount = (categorizedMonitors?.NoMonitoring?.length || 0) + (categorizedMonitors?.['No monitoreo']?.length || 0);
    
    const total = uptimeCount + downtimeCount + partialCount;
    const percentage = total > 0 ? (uptimeCount / total) * 100 : 0;

    return {
      health: {
        totalUptime: uptimeCount,
        total: total,
        totalDowntime: downtimeCount,
        totalPartialtime: partialCount,
        totalNoMonitoring: noMonitoringCount,
        porcentajeSalud: percentage
      }
    };
  }, [categorizedMonitors]);

  const dispKpi = React.useMemo(() => {
    const hasUptime = kpiData?.data?.uptime !== undefined && kpiData?.data?.uptime !== null;
    let totalUptime = 0;
    
    if (hasUptime) {
       totalUptime = kpiData.data.uptime 
                   + (kpiData.data.parcial || 0) 
                   + (kpiData.data.evento_cliente || 0) 
                   + (kpiData.data.marcado_atentus || 0) 
                   + (kpiData.data['no monitoreo'] || 0);
       totalUptime = Math.min(100, parseFloat(totalUptime.toFixed(2)));
    }

    return {
      id: 'disponibilidad',
      labelKey: 'ind.tab.disponibilidad',
      value: hasUptime ? `${totalUptime}%` : '-',
      trendValue: '0%', 
      trendType: 'up',
      color: '#10B981',
      sparklineData: hasUptime ? [totalUptime, totalUptime, totalUptime] : [],
      loading: isLoadingKpis,
      details: hasUptime ? [
        { label: 'Uptime Base', value: `${kpiData.data.uptime}%` },
        { label: 'Parcial', value: `${kpiData.data.parcial}%` },
        { label: 'Evento Cliente', value: `${kpiData.data.evento_cliente}%` },
        { label: 'Marcado Atentus', value: `${kpiData.data.marcado_atentus}%` },
        { label: 'No Monit.', value: `${kpiData.data['no monitoreo']}%` }
      ] : []
    };
  }, [kpiData, isLoadingKpis]);

  const downKpi = React.useMemo(() => {
    const hasDowntime = kpiData?.data?.downtime !== undefined && kpiData?.data?.downtime !== null;
    return {
      id: 'downtime',
      labelKey: 'objective.status.downtime',
      value: hasDowntime ? `${kpiData.data.downtime}%` : '-',
      trendValue: '0%',
      trendType: 'down',
      color: '#EF4444',
      sparklineData: hasDowntime ? [kpiData.data.downtime, kpiData.data.downtime, kpiData.data.downtime] : [],
      loading: isLoadingKpis
    };
  }, [kpiData, isLoadingKpis]);

  const tiempoKpi = React.useMemo(() => {
    let tiempoValue = '-';
    let tiempoSparkline = [];
    const hasRendimientoData = rendimientoData?.Objetivo?.Paso;
    if (hasRendimientoData) {
      const pasos = rendimientoData.Objetivo.Paso;
      const aggregateData = {};
      pasos.forEach(paso => {
        (paso.rendimientoDetalle || []).forEach(det => {
          if (!aggregateData[det.inicio]) aggregateData[det.inicio] = 0;
          aggregateData[det.inicio] += det.respuesta || 0;
        });
      });
      tiempoSparkline = Object.values(aggregateData).map(v => Number(v.toFixed(2)));
      if (tiempoSparkline.length > 0) {
        tiempoValue = `${tiempoSparkline[tiempoSparkline.length - 1]}s`;
      }
    }
    return {
      id: 'tiempo',
      labelKey: 'home.kpis.responseTime',
      value: tiempoValue,
      trendValue: '0%',
      trendType: 'up',
      color: '#6366F1',
      sparklineData: tiempoSparkline,
      loading: isLoadingRendimiento
    };
  }, [rendimientoData, isLoadingRendimiento]);

  const caidasKpi = React.useMemo(() => {
    let outageCount = '-';
    let caidasSparkline = [];
    const globalEvents = disponibilidadDetalle?.Objetivo?.Global?.[0]?.estado;
    if (Array.isArray(globalEvents)) {
      const downtimeEvents = globalEvents.filter(e => e?.nombre?.toLowerCase() === 'downtime');
      outageCount = downtimeEvents.length.toString();
      
      const now = new Date();
      caidasSparkline = Array.from({ length: 24 }, (_, i) => {
        const hourStart = new Date(now.getTime() - (23 - i) * 3600000);
        const hourEnd = new Date(now.getTime() - (22 - i) * 3600000);
        return downtimeEvents.filter(e => {
          const eventStart = new Date(e.fecha_desde);
          return eventStart >= hourStart && eventStart < hourEnd;
        }).length;
      });
    }
    return {
      id: 'caidas',
      labelKey: 'home.kpis.globalOutages',
      value: outageCount,
      trendValue: '0%',
      trendType: 'up',
      color: outageCount !== '-' && parseInt(outageCount) > 0 ? '#F59E0B' : '#10B981',
      sparklineData: caidasSparkline,
      loading: isLoadingDetalle
    };
  }, [disponibilidadDetalle, isLoadingDetalle]);

  const kpis = React.useMemo(() => [dispKpi, downKpi, tiempoKpi, caidasKpi], [dispKpi, downKpi, tiempoKpi, caidasKpi]);

  const HeaderBadge = React.useMemo(() => (
    <Box className={classes.badgeCounter}>
      <Typography variant="caption" style={{ fontWeight: 600, color: '#475569' }}>
        {t('home.events.eventsCount', { count: activeAlerts.length })}
      </Typography>
    </Box>
  ), [activeAlerts.length, classes.badgeCounter, t]);

  const BenchmarkBadge = () =>{
      return <Box className={classes.badgeCounter} style={{ backgroundColor: '#F3F4F6', borderColor: '#E5E7EB' }}>
        <Typography variant="caption" style={{ fontWeight: 600, color: '#6B7280' }}>
          {t('periods.text.720', { defaultValue: 'Último mes' })}
        </Typography>
      </Box>
    };

  return (
    <Box className={classes.mainContainer}>

      <Grid container spacing={2} className={classes.gridSection} alignItems="stretch">
        <Grid item xs={12} lg={6} className={classes.gridItem}>
          <HomePanel title={t('home.panels.systemHealth')} helpText={t('home.help.systemHealth')}>
            <SystemHealth salud={dataPanels.health} loading={isLoadingObjectives} />
          </HomePanel>
        </Grid>

        <Grid item xs={12} lg={6} className={classes.gridItem}>
          <HomePanel
            title={<PanelTitleWithObjective labelKey="home.panels.kpis" objectiveName={objectiveName} to={objectiveId ? { pathname: `/online/${objectiveId}/${(objectiveName || 'obj').replaceAll('/', '|')}/${objective?.nombre_servicio_unificado || 'Todos'}`, search: getQueryParams(), state: { fromObjective: objective } } : undefined} />}
            helpText={t('home.help.kpis')}
          >
            <Grid container spacing={2} style={{ flex: 1 }}>
              <KpisObjetivos kpis={kpis}/>
            </Grid>
          </HomePanel>
        </Grid>
      </Grid>

      <Box className={classes.shortcutsWrapper} my={2}>
        <HomeShortcuts />
      </Box>

      <Grid container spacing={2} className={classes.gridSection} alignItems="stretch">
        <Grid item xs={12} lg={6} className={classes.gridItem}>
          <HomePanel title={<Link to={{ pathname: "/quickviewisplights", search: getQueryParams() }} className={classes.linkHover}>{t('home.panels.latestEvents')}</Link>} headerAction={HeaderBadge} helpText={t('home.help.latestEvents')}>
            <AlertasActivas 
              alertas={activeAlerts || []} 
              onAlertClick={handleOpenModal}
              loading={isLoadingObjectives || isLoadingAlerts}
            />
          </HomePanel>
        </Grid>

        <Grid item xs={12} lg={6} className={classes.gridItem}>
          <HomePanel
            title={<PanelTitleWithObjective labelKey="home.panels.apdex" objectiveName={objectiveName} to={{ pathname: "/apdex", search: getQueryParams(), query: { objetivoId: objectiveId } }} />}
            helpText={t('home.help.apdex')}
          >
            <ObjectivesApdexPanel objective={objective} />
          </HomePanel>
        </Grid>
      </Grid>
      <Grid container spacing={2} className={classes.gridSection} alignItems="stretch">
        <Grid item xs={12} lg={6} className={classes.gridItem}>
          <HomePanel title={t('home.panels.benchmark')} headerAction={BenchmarkBadge} helpText={t('home.help.benchmark')}>
            <BenchmarkPanel objectiveId={objectiveId}/>
          </HomePanel>
        </Grid>
        <Grid item xs={12} lg={6} className={classes.gridItem}>
          <HomePanel title={<Link to={{ pathname: "/quickview", search: getQueryParams() }} className={classes.linkHover}>{t('home.panels.servicesStatus')}</Link>} helpText={t('home.help.servicesStatus')}>
            <HoneycombPanel data={monitorsData} loading={isLoadingObjectives} />
          </HomePanel>
        </Grid>
      </Grid>

      
      <Dialog
        maxWidth="lg"
        fullWidth
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          {modalOpen ? (
            <ListObjectiveModal 
              obj={modalData.id} 
              objName={modalData.name} 
              listObjectives={categorizedMonitors} 
              handleClose={handleCloseModal}
            />
          ) : <div />}
        </Fade>
      </Dialog>
    </Box>
  );
}